import i18n from 'i18next';

export type RolePreset = {
  position?: 'sales' | 'support' | 'consultant' | 'hr';
  gender?: 'male' | 'female';
  name?: string;
  goal?: 'consult' | 'leads' | 'answers';
};

export type StylePreset = {
  addressing?: 'you' | 'youPlural';
  tone?: 'neutral' | 'friendly' | 'business' | 'joking' | 'empathic';
  emojis?: 'none' | 'rare' | 'often';
  length?: 'short' | 'medium' | 'long';
};

export const ROLE_POSITION_OPTIONS: Array<{
  value: NonNullable<RolePreset['position']>;
  labelKey: string;
  textKey: string;
}> = [
  {
    value: 'sales',
    labelKey: 'rolePresets.position.sales.label',
    textKey: 'rolePresets.position.sales.text',
  },
  {
    value: 'support',
    labelKey: 'rolePresets.position.support.label',
    textKey: 'rolePresets.position.support.text',
  },
  {
    value: 'consultant',
    labelKey: 'rolePresets.position.consultant.label',
    textKey: 'rolePresets.position.consultant.text',
  },
  {
    value: 'hr',
    labelKey: 'rolePresets.position.hr.label',
    textKey: 'rolePresets.position.hr.text',
  },
];

export const ROLE_GENDER_OPTIONS: Array<{
  value: NonNullable<RolePreset['gender']>;
  labelKey: string;
  textKey: string;
}> = [
  {
    value: 'male',
    labelKey: 'rolePresets.gender.male.label',
    textKey: 'rolePresets.gender.male.text',
  },
  {
    value: 'female',
    labelKey: 'rolePresets.gender.female.label',
    textKey: 'rolePresets.gender.female.text',
  },
];

export const ROLE_GOAL_OPTIONS: Array<{
  value: NonNullable<RolePreset['goal']>;
  labelKey: string;
  textKey: string;
}> = [
  {
    value: 'consult',
    labelKey: 'rolePresets.goal.consult.label',
    textKey: 'rolePresets.goal.consult.text',
  },
  {
    value: 'leads',
    labelKey: 'rolePresets.goal.leads.label',
    textKey: 'rolePresets.goal.leads.text',
  },
  {
    value: 'answers',
    labelKey: 'rolePresets.goal.answers.label',
    textKey: 'rolePresets.goal.answers.text',
  },
];

export const STYLE_ADDRESSING_OPTIONS: Array<{
  value: NonNullable<StylePreset['addressing']>;
  labelKey: string;
}> = [
  { value: 'you', labelKey: 'stylePresets.addressing.you.label' },
  { value: 'youPlural', labelKey: 'stylePresets.addressing.youPlural.label' },
];

export const STYLE_TONE_OPTIONS: Array<{
  value: NonNullable<StylePreset['tone']>;
  labelKey: string;
  textKey: string;
}> = [
  {
    value: 'neutral',
    labelKey: 'stylePresets.tone.neutral.label',
    textKey: 'stylePresets.tone.neutral.text',
  },
  {
    value: 'friendly',
    labelKey: 'stylePresets.tone.friendly.label',
    textKey: 'stylePresets.tone.friendly.text',
  },
  {
    value: 'business',
    labelKey: 'stylePresets.tone.business.label',
    textKey: 'stylePresets.tone.business.text',
  },
  {
    value: 'joking',
    labelKey: 'stylePresets.tone.joking.label',
    textKey: 'stylePresets.tone.joking.text',
  },
  {
    value: 'empathic',
    labelKey: 'stylePresets.tone.empathic.label',
    textKey: 'stylePresets.tone.empathic.text',
  },
];

export const STYLE_EMOJI_OPTIONS: Array<{
  value: NonNullable<StylePreset['emojis']>;
  labelKey: string;
  textKey: string;
}> = [
  {
    value: 'none',
    labelKey: 'stylePresets.emojis.none.label',
    textKey: 'stylePresets.emojis.none.text',
  },
  {
    value: 'rare',
    labelKey: 'stylePresets.emojis.rare.label',
    textKey: 'stylePresets.emojis.rare.text',
  },
  {
    value: 'often',
    labelKey: 'stylePresets.emojis.often.label',
    textKey: 'stylePresets.emojis.often.text',
  },
];

export const STYLE_LENGTH_OPTIONS: Array<{
  value: NonNullable<StylePreset['length']>;
  labelKey: string;
  textKey: string;
}> = [
  {
    value: 'short',
    labelKey: 'stylePresets.length.short.label',
    textKey: 'stylePresets.length.short.text',
  },
  {
    value: 'medium',
    labelKey: 'stylePresets.length.medium.label',
    textKey: 'stylePresets.length.medium.text',
  },
  {
    value: 'long',
    labelKey: 'stylePresets.length.long.label',
    textKey: 'stylePresets.length.long.text',
  },
];

const t = (key: string, options?: Record<string, unknown>) =>
  i18n.t(`translation:prompt.${key}`, options);

export const getRoleNameOptions = (gender?: RolePreset['gender']) => {
  if (!gender) return [];

  return t(`rolePresets.name.${gender}`, { returnObjects: true }) as unknown as string[];
};

export const getRoleMarker = (key: 'position' | 'gender' | 'name' | 'goal') =>
  t(`rolePresets.markers.${key}`);

export const getStyleMarker = (key: 'addressing' | 'tone' | 'emojis' | 'length') =>
  t(`stylePresets.markers.${key}`);

export const getRoleMarkers = () => [
  getRoleMarker('position'),
  getRoleMarker('gender'),
  getRoleMarker('name'),
  getRoleMarker('goal'),
];

export const getStyleMarkers = () => [
  getStyleMarker('addressing'),
  getStyleMarker('tone'),
  getStyleMarker('emojis'),
  getStyleMarker('length'),
];

export const buildRoleSegment = (part: 'position' | 'gender' | 'name' | 'goal', preset: RolePreset) => {
  if (part === 'position' && preset.position) {
    return t('rolePresets.phrases.positionSegment', {
      marker: getRoleMarker('position'),
      position: t(`rolePresets.position.${preset.position}.text`),
    });
  }

  if (part === 'gender' && preset.gender) {
    return t('rolePresets.phrases.genderSegment', {
      marker: getRoleMarker('gender'),
      gender: t(`rolePresets.gender.${preset.gender}.text`),
    });
  }

  if (part === 'name' && preset.name) {
    return t('rolePresets.phrases.nameSegment', {
      marker: getRoleMarker('name'),
      name: preset.name,
    });
  }

  if (part === 'goal' && preset.goal) {
    return t('rolePresets.phrases.goalSegment', {
      marker: getRoleMarker('goal'),
      goal: t(`rolePresets.goal.${preset.goal}.text`),
    });
  }

  return '';
};

export const buildStyleSegment = (
  part: 'addressing' | 'tone' | 'emojis' | 'length',
  preset: StylePreset,
) => {
  if (part === 'addressing' && preset.addressing) {
    return t('stylePresets.phrases.addressingSegment', {
      marker: getStyleMarker('addressing'),
      addressing: t(`stylePresets.addressing.${preset.addressing}.text`),
    });
  }

  if (part === 'tone' && preset.tone) {
    return t('stylePresets.phrases.toneSegment', {
      marker: getStyleMarker('tone'),
      tone: t(`stylePresets.tone.${preset.tone}.text`),
    });
  }

  if (part === 'emojis' && preset.emojis) {
    return t('stylePresets.phrases.emojisSegment', {
      marker: getStyleMarker('emojis'),
      emojis: t(`stylePresets.emojis.${preset.emojis}.text`),
    });
  }

  if (part === 'length' && preset.length) {
    return t('stylePresets.phrases.lengthSegment', {
      marker: getStyleMarker('length'),
      length: t(`stylePresets.length.${preset.length}.text`),
    });
  }

  return '';
};

const extractSegment = (source: string, marker: string, markers: string[]) => {
  const startIndex = source.indexOf(marker);
  if (startIndex === -1) return null;

  let endIndex = source.length;
  markers.forEach((nextMarker) => {
    if (nextMarker === marker) return;
    const nextIndex = source.indexOf(nextMarker, startIndex + marker.length);
    if (nextIndex !== -1 && nextIndex < endIndex) {
      endIndex = nextIndex;
    }
  });

  return source.slice(startIndex, endIndex);
};

export const parseRolePreset = (text: string): RolePreset => {
  const preset: RolePreset = {};
  const source = text || '';
  const markers = getRoleMarkers();

  ROLE_POSITION_OPTIONS.forEach((option) => {
    const fragment = t(option.textKey);
    const segment = extractSegment(source, getRoleMarker('position'), markers);
    if (segment && fragment && segment.includes(fragment)) {
      preset.position = option.value;
    }
  });

  ROLE_GENDER_OPTIONS.forEach((option) => {
    const fragment = t(option.textKey);
    const segment = extractSegment(source, getRoleMarker('gender'), markers);
    if (segment && fragment && segment.includes(fragment)) {
      preset.gender = option.value;
    }
  });

  (['male', 'female'] as const).forEach((gender) => {
    const names = getRoleNameOptions(gender);
    names.forEach((name) => {
      const segment = extractSegment(source, getRoleMarker('name'), markers);
      const fullSegment = t('rolePresets.phrases.nameSegment', {
        marker: getRoleMarker('name'),
        name,
      });

      if (segment && (segment.includes(name) || segment.includes(fullSegment))) {
        preset.name = name;
        preset.gender = preset.gender ?? gender;
      }
    });
  });

  ROLE_GOAL_OPTIONS.forEach((option) => {
    const fragment = t(option.textKey);
    const segment = extractSegment(source, getRoleMarker('goal'), markers);
    if (segment && fragment && segment.includes(fragment)) {
      preset.goal = option.value;
    }
  });

  return preset;
};

export const hasRoleMarker = (
  text: string,
  key: 'position' | 'gender' | 'name' | 'goal',
) => text.includes(getRoleMarker(key));

export const parseRolePresetWithMarkers = (
  text: string,
  currentPreset: RolePreset,
): RolePreset => {
  const parsed = parseRolePreset(text);
  const nextPreset: RolePreset = { ...currentPreset };

  if (!hasRoleMarker(text, 'position')) {
    nextPreset.position = undefined;
    nextPreset.gender = undefined;
    nextPreset.name = undefined;
    nextPreset.goal = undefined;
  } else if (parsed.position) {
    nextPreset.position = parsed.position;
  }

  if (!hasRoleMarker(text, 'gender')) {
    nextPreset.gender = undefined;
    nextPreset.name = undefined;
    nextPreset.goal = undefined;
  } else if (parsed.gender) {
    nextPreset.gender = parsed.gender;
  }

  if (!hasRoleMarker(text, 'name')) {
    nextPreset.name = undefined;
    nextPreset.goal = undefined;
  } else if (parsed.name) {
    nextPreset.name = parsed.name;
  }

  if (!hasRoleMarker(text, 'goal')) {
    nextPreset.goal = undefined;
  } else if (parsed.goal) {
    nextPreset.goal = parsed.goal;
  }

  return nextPreset;
};

export const parseStylePreset = (text: string): StylePreset => {
  const preset: StylePreset = {};
  const source = text || '';
  const markers = getStyleMarkers();

  STYLE_ADDRESSING_OPTIONS.forEach((option) => {
    const addressing = t(`stylePresets.addressing.${option.value}.text`);
    const fragment = t('stylePresets.phrases.addressingSegment', {
      marker: getStyleMarker('addressing'),
      addressing,
    });
    const segment = extractSegment(source, getStyleMarker('addressing'), markers);
    if (segment && fragment && segment.includes(fragment)) {
      preset.addressing = option.value;
    }
  });

  STYLE_TONE_OPTIONS.forEach((option) => {
    const fragment = t(option.textKey);
    const segment = extractSegment(source, getStyleMarker('tone'), markers);
    if (segment && fragment && segment.includes(fragment)) {
      preset.tone = option.value;
    }
  });

  STYLE_EMOJI_OPTIONS.forEach((option) => {
    const fragment = t(option.textKey);
    const segment = extractSegment(source, getStyleMarker('emojis'), markers);
    if (segment && fragment && segment.includes(fragment)) {
      preset.emojis = option.value;
    }
  });

  STYLE_LENGTH_OPTIONS.forEach((option) => {
    const fragment = t(option.textKey);
    const segment = extractSegment(source, getStyleMarker('length'), markers);
    if (segment && fragment && segment.includes(fragment)) {
      preset.length = option.value;
    }
  });

  return preset;
};

export const hasStyleMarker = (
  text: string,
  key: 'addressing' | 'tone' | 'emojis' | 'length',
) => text.includes(getStyleMarker(key));

export const parseStylePresetWithMarkers = (
  text: string,
  currentPreset: StylePreset,
): StylePreset => {
  const parsed = parseStylePreset(text);
  const nextPreset: StylePreset = { ...currentPreset };

  if (!hasStyleMarker(text, 'addressing')) {
    nextPreset.addressing = undefined;
    nextPreset.tone = undefined;
    nextPreset.emojis = undefined;
    nextPreset.length = undefined;
  } else if (parsed.addressing) {
    nextPreset.addressing = parsed.addressing;
  }

  if (!hasStyleMarker(text, 'tone')) {
    nextPreset.tone = undefined;
    nextPreset.emojis = undefined;
    nextPreset.length = undefined;
  } else if (parsed.tone) {
    nextPreset.tone = parsed.tone;
  }

  if (!hasStyleMarker(text, 'emojis')) {
    nextPreset.emojis = undefined;
    nextPreset.length = undefined;
  } else if (parsed.emojis) {
    nextPreset.emojis = parsed.emojis;
  }

  if (!hasStyleMarker(text, 'length')) {
    nextPreset.length = undefined;
  } else if (parsed.length) {
    nextPreset.length = parsed.length;
  }

  return nextPreset;
};

export const ROLE_GROUP_LABEL_KEYS = {
  position: 'rolePresets.labels.position',
  gender: 'rolePresets.labels.gender',
  name: 'rolePresets.labels.name',
  goal: 'rolePresets.labels.goal',
};

export const STYLE_GROUP_LABEL_KEYS = {
  addressing: 'stylePresets.labels.addressing',
  tone: 'stylePresets.labels.tone',
  emojis: 'stylePresets.labels.emojis',
  length: 'stylePresets.labels.length',
};
